import { API_BASE_URL } from '@config/constants';
import { fetchWithSupabaseAuth } from '@services/supabase-auth-fetch.service';

export interface BillingPaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  funding: string | null;
  country: string | null;
  isDefault: boolean;
}

export interface BillingInvoice {
  id: string;
  number: string | null;
  amountDue: number;
  amountPaid: number;
  currency: string | null;
  status: string | null;
  created: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
  /** Cents; storage add-on lines only, when returned by API */
  addonLineAmount?: number | null;
  /** Cents; amountDue minus add-on lines when breakdown is available */
  nonAddonAmount?: number | null;
}

/** Stripe subscription 100% courtesy coupon (Support); not the same as plan_expires_at */
export interface BillingCourtesyInfo {
  active: boolean;
  appliesFromPeriodEnd: string | null;
  courtesyEndsAt: string | null;
  isPermanent: boolean;
  note: string | null;
}

export interface BillingOverviewResponse {
  plan: 'free' | 'bedroom' | 'pro' | 'studio';
  billingInterval: 'monthly' | 'yearly';
  /** Admin temporary plan end (not Stripe billing cycle) */
  planExpiresAt?: string | null;
  billingCourtesy?: BillingCourtesyInfo;
  subscriptionStatus: string | null;
  subscriptionRenewsAt: string | null;
  /** True when the main plan subscription is set to end (Stripe cancel_at_period_end or future cancel_at). */
  subscriptionCancelAtPeriodEnd: boolean | null;
  storageLimit: number | null;
  storageUsed: number | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  paymentMethods: BillingPaymentMethod[];
  invoices: BillingInvoice[];
  upcomingInvoice: BillingInvoice | null;
  /** Upcoming invoice for a separate Stripe subscription that only bills storage add-ons. */
  addonUpcomingInvoice?: BillingInvoice | null;
  defaultPaymentMethodId: string | null;
  customerEmail: string | null;
  customerName: string | null;
  currency: string | null;
  storageAddons?: Array<{
    addonId: 'addon_250gb' | 'addon_500gb';
    name: string;
    storageBytes: number;
    interval: 'monthly' | 'yearly';
  }>;
  storageAddonsTotalBytes?: number;
  /** When set, extra add-on storage ends at this time (scheduled Stripe change). */
  storageAddonCancelAt?: string | null;
}

export interface BillingOverviewRequest {
  userId?: string | null;
  email?: string | null;
  signal?: AbortSignal;
}

export interface CancelSubscriptionRequest {
  userId?: string | null;
  email?: string | null;
  cancelAtPeriodEnd?: boolean;
}

export const fetchBillingOverview = async ({
  userId,
  email,
  signal,
}: BillingOverviewRequest): Promise<BillingOverviewResponse> => {
  const url = new URL(`${API_BASE_URL}/billing/overview`);

  if (userId) {
    url.searchParams.set('userId', userId);
  }
  if (email) {
    url.searchParams.set('email', email);
  }

  const response = await fetchWithSupabaseAuth(url.toString(), {
    method: 'GET',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Billing overview request failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as BillingOverviewResponse;
  return data;
};

export const cancelBillingSubscription = async ({
  cancelAtPeriodEnd = true,
}: CancelSubscriptionRequest = {}): Promise<BillingOverviewResponse> => {
  const response = await fetchWithSupabaseAuth(`${API_BASE_URL}/billing/cancel`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      cancelAtPeriodEnd,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Subscription cancellation failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as BillingOverviewResponse;
  return data;
};

export interface ReactivateSubscriptionRequest {
  userId?: string | null;
  email?: string | null;
}

export interface DeletePaymentMethodRequest {
  paymentMethodId: string;
}

export const reactivateBillingSubscription = async (
  _params?: ReactivateSubscriptionRequest,
): Promise<BillingOverviewResponse> => {
  const response = await fetchWithSupabaseAuth(`${API_BASE_URL}/billing/reactivate`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Subscription reactivation failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as BillingOverviewResponse;
  return data;
};

export const deletePaymentMethod = async ({
  paymentMethodId,
}: DeletePaymentMethodRequest): Promise<BillingOverviewResponse> => {
  const response = await fetchWithSupabaseAuth(
    `${API_BASE_URL}/billing/payment-methods/${encodeURIComponent(paymentMethodId)}`,
    {
      method: 'DELETE',
      credentials: 'include',
    },
  );

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Failed to delete payment method (status ${response.status}).`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as BillingOverviewResponse;
  return data;
};

export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  const response = await fetchWithSupabaseAuth(`${API_BASE_URL}/billing/portal`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Billing portal request failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as { url?: string };
  if (!data.url) {
    throw new Error('Billing portal URL missing.');
  }
  return { url: data.url };
};

export const mutateStorageAddon = async (params: {
  addonId: 'addon_250gb' | 'addon_500gb';
  action: 'add' | 'remove';
  billingInterval?: 'monthly' | 'yearly';
}): Promise<BillingOverviewResponse> => {
  const response = await fetchWithSupabaseAuth(`${API_BASE_URL}/billing/addons/mutate`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Storage add-on update failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return (await response.json()) as BillingOverviewResponse;
};

export const reactivateStorageAddon = async (): Promise<BillingOverviewResponse> => {
  const response = await fetchWithSupabaseAuth(`${API_BASE_URL}/billing/addons/reactivate`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Storage add-on reactivation failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return (await response.json()) as BillingOverviewResponse;
};

export const createAddonCheckout = async (params: {
  addonId: 'addon_250gb' | 'addon_500gb';
}): Promise<{ url: string }> => {
  const response = await fetchWithSupabaseAuth(`${API_BASE_URL}/billing/addons/checkout`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Storage add-on checkout failed with status ${response.status}`;
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as { url?: string };
  if (!data.url) {
    throw new Error('Add-on checkout URL missing.');
  }
  return { url: data.url };
};

export interface StripePriceData {
  planId: 'bedroom' | 'pro' | 'studio' | 'addon_250gb' | 'addon_500gb';
  interval: 'monthly' | 'yearly';
  priceId: string;
  amount: number; // in cents
  currency: string;
  unitAmount: number; // in smallest currency unit (e.g., cents for EUR)
  formattedAmount: string; // formatted price string
}

export interface StripePricesResponse {
  prices: StripePriceData[];
}

export const fetchStripePrices = async (signal?: AbortSignal): Promise<StripePricesResponse> => {
  const url = `${API_BASE_URL}/billing/prices`;
  console.log('🔗 [billing.service] Fetching Stripe prices from:', url);
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    signal,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message =
      errorBody?.message ??
      `Failed to fetch Stripe prices with status ${response.status}`;
    console.error('❌ [billing.service] Failed to fetch prices:', response.status, message);
    const error = new Error(message);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  const data = (await response.json()) as StripePricesResponse;
  console.log('✅ [billing.service] Successfully fetched prices:', data.prices?.length || 0, 'prices');
  return data;
};

